// src/components/WorkforceManagementConfig.jsx
import React, { useState, useRef, useEffect } from 'react';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
    Typography, Box,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, IconButton, Stack, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, Button, Snackbar, Alert,
    CircularProgress, Menu, MenuItem, Avatar,
    FormControlLabel, Checkbox,
    FormControl,
    InputLabel,
    Select,
    Chip // Import Chip for deletable roles
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined"; // Corrected import path
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import CloseIcon from '@mui/icons-material/Close';

import { database } from "../firebase"; // Asumsi path ke firebase.js Anda
import { ref, push, set, onValue, update, remove, query, orderByChild, equalTo } from "firebase/database";

// Helper: ubah onValue menjadi Promise
const onValuePromise = (refTarget) => {
    return new Promise((resolve, reject) => {
        onValue(
            refTarget,
            (snapshot) => resolve(snapshot),
            (error) => reject(error),
            { onlyOnce: true }
        );
    });
};

function WorkforceManagementConfig({ title, dbPath, fieldName, primaryKeyField }) {
    const [dataList, setDataList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [openDialog, setOpenDialog] = useState(false);
    const [fieldValue, setFieldValue] = useState("");
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [currentId, setCurrentId] = useState(null);

    // State tambahan untuk user data di dialog
    const [userGender, setUserGender] = useState('');
    const [initialUserGender, setInitialUserGender] = useState('');
    const [userJabatan, setUserJabatan] = useState('');
    const [initialUserJabatan, setInitialUserJabatan] = useState(''); // New initial state for Jabatan
    const [userStatusPekerjaan, setUserStatusPekerjaan] = useState('');
    const [initialUserStatusPekerjaan, setInitialUserStatusPekerjaan] = useState(''); // New initial state for Status Pekerjaan
    const [userStatusTenagaKerja, setUserStatusTenagaKerja] = useState('');
    const [initialUserStatusTenagaKerja, setInitialUserStatusTenagaKerja] = useState('');
    const [userEmail, setUserEmail] = useState('');
    const [userAvatar, setUserAvatar] = useState('');
    const [userIndex, setUserIndex] = useState(''); // Corrected: Initialized with useState
    const [userDepartment, setUserDepartment] = useState('');
    const [initialUserDepartment, setInitialUserDepartment] = useState('');

    // New states for Roles
    const [userRoles, setUserRoles] = useState([]); // Array of role IDs
    const [initialUserRoles, setInitialUserRoles] = useState([]);
    const [roleList, setRoleList] = useState([]); // List of all roles from db_tb_role
    const [openRoleDialog, setOpenRoleDialog] = useState(false); // For "Tambah Role" dialog
    const [selectedRolesInDialog, setSelectedRolesInDialog] = useState([]); // Temp selection in dialog

    // State for role chip delete confirmation
    const [openRoleDeleteConfirmDialog, setOpenRoleDeleteConfirmDialog] = useState(false);
    const [roleToDeleteId, setRoleToDeleteId] = useState(null);
    const [roleToDeleteName, setRoleToDeleteName] = useState(''); // Corrected: useState('') instead of = ''

    // New states for Flow Configurations
    const [flowConfigurations, setFlowConfigurations] = useState([]);
    const [userFlowOrderDefault, setUserFlowOrderDefault] = useState(''); // New state for user's default flow
    const [initialUserFlowOrderDefault, setInitialUserFlowOrderDefault] = useState(''); // New initial state


    const [departments, setDepartments] = useState([]);
    const [jabatanList, setJabatanList] = useState([]);
    const [statusPekerjaanList, setStatusPekerjaanList] = useState([]);
    const [statusTenagaKerjaList, setStatusTenagaKerjaList] = useState([]);

    // State untuk mode detail dan edit di dialog user
    const [isDetailView, setIsDetailView] = useState(false);
    const [isEditableDetail, setIsEditableDetail] = useState(false);

    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });

    const fileInputRef = useRef(null);

    const isIndexTable = dbPath === "db_config/db_wfms/tb_index";
    const isEmailTable = dbPath === "db_config/db_wfms/tb_email";
    const isUserTable = dbPath === "db_user/tb_user";

    // Ambil data dari Firebase
    useEffect(() => {
        const tbRef = ref(database, dbPath);
        setLoading(true);

        const unsubscribe = onValue(
            tbRef,
            (snapshot) => {
                const data = snapshot.val();
                if (snapshot.exists()) {
                    if (isUserTable) {
                        const users = [];
                        Object.keys(data).forEach(uid => {
                            const userData = data[uid];
                            users.push({
                                uid: uid,
                                nama_user: userData.name || '',
                                email: userData.email || '',
                                gender: userData.gender || '',
                                jabatan: userData.id_jabatan || '',
                                department: userData.id_department || '',
                                status_pekerjaan: userData.id_status_pekerjaan || '',
                                status_tenaga_kerja: userData.id_status_tenaga_kerja || '',
                                roles: userData.id_role ? userData.id_role.split(';') : [], // Handle multiple roles
                                avatar: userData.avatar || '',
                                id_flow_order_default: userData.id_flow_order_default || '' // Fetch new property
                            });
                        });
                        setDataList(users);
                    } else {
                        const list = Object.values(data);
                        setDataList(list);
                    }
                } else {
                    setDataList([]);
                }
                setLoading(false);
            },
            (error) => {
                console.error("Error:", error);
                setSnackbar({
                    open: true,
                    message: `Gagal memuat data ${title.toLowerCase()}.`,
                    severity: "error",
                });
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, [dbPath, title, isUserTable]);

    // Fetch departments, jabatan, status pekerjaan, status tenaga kerja, roles, and flow configurations for dropdowns
    useEffect(() => {
        const fetchConfigData = async () => {
            // Fetch Departments
            const departmentRef = ref(database, "db_config/db_wfms/tb_department");
            const departmentSnapshot = await onValuePromise(departmentRef);
            const departmentData = departmentSnapshot.val();
            if (departmentData) {
                const departmentsList = Object.keys(departmentData).map(key => ({
                    id: key,
                    name: departmentData[key].nama_department
                }));
                setDepartments(departmentsList);
            }

            // Fetch Jabatan
            const jabatanRef = ref(database, "db_config/db_wfms/tb_jabatan");
            const jabatanSnapshot = await onValuePromise(jabatanRef);
            const jabatanData = jabatanSnapshot.val();
            if (jabatanData) {
                const jabatanList = Object.keys(jabatanData).map(key => ({
                    id: key,
                    name: jabatanData[key].nama_jabatan
                }));
                setJabatanList(jabatanList);
            }

            // Fetch Status Pekerjaan
            const statusPekerjaanRef = ref(database, "db_config/db_wfms/tb_status_pekerjaan");
            const statusPekerjaanSnapshot = await onValuePromise(statusPekerjaanRef);
            const statusList = statusPekerjaanSnapshot.val();
            if (statusList) {
                const statusPekerjaanList = Object.keys(statusList).map(key => ({
                    id: key,
                    name: statusList[key].nama_status_pekerjaan
                }));
                setStatusPekerjaanList(statusPekerjaanList);
            }

            // Fetch Status Tenaga Kerja
            const statusTenagaKerjaRef = ref(database, "db_config/db_wfms/tb_status_tenaga_kerja");
            const statusTenagaKerjaSnapshot = await onValuePromise(statusTenagaKerjaRef);
            const statusTenagaKerjaData = statusTenagaKerjaSnapshot.val();
            if (statusTenagaKerjaData) {
                const statusTenagaKerjaList = Object.keys(statusTenagaKerjaData).map(key => ({
                    id: key,
                    name: statusTenagaKerjaData[key].nama_status_tenaga_kerja
                }));
                setStatusTenagaKerjaList(statusTenagaKerjaList);
            }

            // New: Fetch Roles
            const roleRef = ref(database, "db_config/db_wfms/tb_role");
            const roleSnapshot = await onValuePromise(roleRef);
            const roleData = roleSnapshot.val();
            if (roleData) {
                const roles = Object.keys(roleData).map(key => ({
                    id: key,
                    name: roleData[key].nama_role
                }));
                setRoleList(roles);
            }

            // New: Fetch Flow Configurations for SCM
            const flowConfigRef = ref(database, "db_config/db_scm/tb_flow_order_barang_expand");
            const flowConfigSnapshot = await onValuePromise(flowConfigRef);
            const flowConfigData = flowConfigSnapshot.val();
            if (flowConfigData) {
                const flows = Object.keys(flowConfigData).map(key => ({
                    id: key,
                    flow_name: flowConfigData[key].flow_name
                }));
                setFlowConfigurations(flows);
            }
        };

        if (isUserTable) {
            fetchConfigData();
        }
    }, [isUserTable]);


    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleOpenDialog = () => {
        setIsEditing(false);
        setIsDetailView(false);
        setIsEditableDetail(false);
        setCurrentId(null);
        setFieldValue("");
        setUserGender('');
        setUserJabatan('');
        setUserDepartment('');
        setUserStatusPekerjaan('');
        setUserStatusTenagaKerja('');
        setUserRoles([]); // Clear roles
        setSelectedRolesInDialog([]); // Clear temp roles
        setUserEmail('');
        setUserAvatar('');
        setUserIndex('');
        setUserFlowOrderDefault(''); // Clear new state
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setFieldValue("");
        setIsEditing(false);
        setIsDetailView(false);
        setIsEditableDetail(false);
        setCurrentId(null);
        setUserGender('');
        setInitialUserGender('');
        setUserJabatan('');
        setInitialUserJabatan(''); // Clear initial Jabatan
        setUserDepartment('');
        setInitialUserDepartment('');
        setUserStatusPekerjaan('');
        setInitialUserStatusPekerjaan(''); // Clear initial Status Pekerjaan
        setUserStatusTenagaKerja('');
        setInitialUserStatusTenagaKerja('');
        setUserRoles([]); // Clear roles
        setInitialUserRoles([]); // Clear initial roles
        setSelectedRolesInDialog([]); // Clear temp roles
        setUserEmail('');
        setUserAvatar('');
        setUserIndex('');
        setUserFlowOrderDefault(''); // Clear new state
        setInitialUserFlowOrderDefault(''); // Clear initial state
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleSave = async () => {
        if (!fieldValue.trim() && !isUserTable) {
            setSnackbar({
                open: true,
                message: `${title.slice(title.indexOf(" ") + 1)} tidak boleh kosong.`,
                severity: "warning",
            });
            return;
        }

        const normalizedFieldValue = fieldValue.toLowerCase().trim();

        if (!isUserTable) {
            const isDuplicate = dataList.some(item =>
                item[fieldName]?.toLowerCase().trim() === normalizedFieldValue &&
                item[primaryKeyField] !== currentId
            );

            if (isDuplicate) {
                setSnackbar({
                    open: true,
                    message: `${title.slice(title.indexOf(" ") + 1)} "${fieldValue}" sudah ada.`,
                    severity: "error",
                });
                return;
            }
        }

        try {
            if (isEditing) {
                const itemRef = ref(database, `${dbPath}/${currentId}`);
                const updatedData = {};

                if (isUserTable) {
                    updatedData.name = fieldValue;
                    updatedData.gender = userGender;
                    updatedData.id_jabatan = userJabatan;
                    updatedData.id_department = userDepartment;
                    updatedData.id_status_pekerjaan = userStatusPekerjaan;
                    updatedData.id_status_tenaga_kerja = userStatusTenagaKerja;
                    updatedData.id_role = userRoles.join(';'); // Save roles as semicolon-separated string
                    updatedData.id_flow_order_default = userFlowOrderDefault; // Save new property
                } else {
                    updatedData[fieldName] = fieldValue;
                }
                await update(itemRef, updatedData);
                setSnackbar({
                    open: true,
                    message: `${title.slice(title.indexOf(" ") + 1)} berhasil diperbarui.`,
                    severity: "success",
                });
            } else {
                if (isUserTable) {
                     setSnackbar({
                        open: true,
                        message: "Penambahan user baru dilakukan melalui sistem autentikasi.",
                        severity: "info",
                    });
                    handleCloseDialog();
                    return;
                }

                const tbRef = ref(database, dbPath);
                const newRef = push(tbRef);
                const key = newRef.key;
                const data = {
                    [primaryKeyField]: key,
                    [fieldName]: fieldValue,
                };
                if (isIndexTable || isEmailTable) {
                    data.user_uid = "";
                }
                await set(newRef, data);
                setSnackbar({
                    open: true,
                    message: `${title.slice(title.indexOf(" ") + 1)} berhasil disimpan.`,
                    severity: "success",
                });
            }
            handleCloseDialog();
        } catch (error) {
            console.error("Gagal simpan/update:", error);
            setSnackbar({
                open: true,
                message: `Gagal menyimpan/memperbarui data ${title.toLowerCase()}.`,
                severity: "error",
            });
        }
    };

    const handleImportButtonClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleImportExcel = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: "array" });

                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet);

                const tbRef = ref(database, dbPath);

                const snapshot = await onValuePromise(tbRef);
                const existingData = snapshot.exists() ? snapshot.val() : {};

                const existingFieldValues = isUserTable
                    ? Object.values(existingData).map(item => item.name?.toLowerCase().trim())
                    : Object.values(existingData).map(item => item[fieldName]?.toLowerCase().trim());

                let importedCount = 0;
                let ignoredCount = 0;

                // Pre-fetch department, jabatan, status_pekerjaan, status_tenaga_kerja, and roles for lookup
                const departmentMap = {};
                departments.forEach(dep => departmentMap[dep.name.toLowerCase()] = dep.id);
                const jabatanMap = {};
                jabatanList.forEach(jab => jabatanMap[jab.name.toLowerCase()] = jab.id);
                const statusPekerjaanMap = {};
                statusPekerjaanList.forEach(status => statusPekerjaanMap[status.name.toLowerCase()] = status.id);
                const statusTenagaKerjaMap = {};
                statusTenagaKerjaList.forEach(status => statusTenagaKerjaMap[status.name.toLowerCase()] = status.id);
                const roleMap = {}; // New map for roles
                roleList.forEach(role => roleMap[role.name.toLowerCase()] = role.id);
                const flowConfigMap = {}; // New map for flow configurations
                flowConfigurations.forEach(flow => flowConfigMap[flow.flow_name.toLowerCase()] = flow.id);


                for (const item of jsonData) {
                    let importedValue;
                    const dataToSave = {};

                    if (isUserTable) {
                        importedValue = item["Nama"];
                        if (!importedValue) {
                            ignoredCount++;
                            continue;
                        }
                        dataToSave.name = importedValue;
                        if (item["Gender"]) dataToSave.gender = item["Gender"];

                        if (item["Jabatan"]) {
                            const jabatanId = jabatanMap[item["Jabatan"].toLowerCase()];
                            if (jabatanId) dataToSave.id_jabatan = jabatanId;
                        }
                        if (item["Departemen"]) {
                            const departmentId = departmentMap[item["Departemen"].toLowerCase()];
                            if (departmentId) dataToSave.id_department = departmentId;
                        }
                        if (item["Status Pekerjaan"]) {
                            const statusPekerjaanId = statusPekerjaanMap[item["Status Pekerjaan"].toLowerCase()];
                            if (statusPekerjaanId) dataToSave.id_status_pekerjaan = statusPekerjaanId;
                        }
                        if (item["Status Tenaga Kerja"]) {
                            const statusTenagaKerjaId = statusTenagaKerjaMap[item["Status Tenaga Kerja"].toLowerCase()];
                            if (statusTenagaKerjaId) dataToSave.id_status_tenaga_kerja = statusTenagaKerjaId;
                        }
                        // New: Handle "Role" during import
                        if (item["Role"]) {
                            const rolesFromExcel = String(item["Role"]).split(';').map(r => r.trim().toLowerCase());
                            const roleIds = rolesFromExcel.map(r => roleMap[r]).filter(id => id);
                            if (roleIds.length > 0) dataToSave.id_role = roleIds.join(';');
                        }
                        // New: Handle "Alur Persetujuan Default" during import
                        if (item["Alur Persetujuan Default"]) {
                            const flowId = flowConfigMap[item["Alur Persetujuan Default"].toLowerCase()];
                            if (flowId) dataToSave.id_flow_order_default = flowId;
                        }

                        if (item["Email"]) dataToSave.email = item["Email"];

                        if (!item["UID"]) {
                             ignoredCount++;
                             continue;
                        }
                        dataToSave.uid = item["UID"];
                        if (Object.keys(existingData).includes(item["UID"]) ||
                            Object.values(existingData).some(existItem => existItem.email === item["Email"])) {
                            ignoredCount++;
                            continue;
                        }
                        await set(ref(database, `${dbPath}/${item["UID"]}`), dataToSave);
                    } else {
                        importedValue = item[title.slice(title.indexOf(" ") + 1)];
                        if (!importedValue) {
                            ignoredCount++;
                            continue;
                        }
                        dataToSave[fieldName] = importedValue;

                        const valueLower = importedValue.toLowerCase().trim();
                        if (existingFieldValues.includes(valueLower)) {
                            ignoredCount++;
                            continue;
                        }
                        const newRef = push(tbRef);
                        const key = newRef.key;
                        dataToSave[primaryKeyField] = key;
                        if (isIndexTable || isEmailTable) {
                            dataToSave.user_uid = "";
                        }
                        await set(newRef, dataToSave);
                    }
                    importedCount++;
                }

                setSnackbar({
                    open: true,
                    message: `Berhasil impor ${importedCount} data baru. ${ignoredCount} data sudah ada/invalid diabaikan.`,
                    severity: "success",
                });
            } catch (error) {
                console.error("Gagal impor Excel:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal mengimpor data dari Excel.",
                    severity: "error",
                });
            } finally {
                event.target.value = null;
            }
        };

        reader.readAsArrayBuffer(file);
    };


    const exportToExcel = () => {
        if (!dataList || dataList.length === 0) {
            setSnackbar({
                open: true,
                message: "Tidak ada data untuk diekspor.",
                severity: "warning",
            });
            return;
        }

        const formattedData = dataList.map((item) => {
            const row = {};
            if (isUserTable) {
                row["UID"] = item.uid;
                row["Nama"] = item.nama_user;
                row["Email"] = item.email;
                row["Gender"] = item.gender;
                // Look up department name from ID
                const departmentName = departments.find(dep => dep.id === item.department)?.name || item.department;
                row["Departemen"] = departmentName;
                // Look up jabatan name from ID
                const jabatanName = jabatanList.find(jab => jab.id === item.jabatan)?.name || item.jabatan;
                row["Jabatan"] = jabatanName;
                // Look up status pekerjaan name from ID
                const statusPekerjaanName = statusPekerjaanList.find(status => status.id === item.status_pekerjaan)?.name || item.status_pekerjaan;
                row["Status Pekerjaan"] = statusPekerjaanName;
                // Look up status tenaga kerja name from ID
                const statusTenagaKerjaName = statusTenagaKerjaList.find(status => status.id === item.status_tenaga_kerja)?.name || item.status_tenaga_kerja;
                row["Status Tenaga Kerja"] = statusTenagaKerjaName;
                // New: Look up role names from IDs
                const roleNames = (item.roles || []).map(roleId => roleList.find(role => role.id === roleId)?.name || roleId).join(';');
                row["Role"] = roleNames;
                // New: Look up flow order default name from ID
                const flowOrderDefaultName = flowConfigurations.find(flow => flow.id === item.id_flow_order_default)?.flow_name || item.id_flow_order_default || 'N/A';
                row["Alur Persetujuan Default"] = flowOrderDefaultName;
                row["Avatar URL"] = item.avatar;
            } else {
                row[primaryKeyField] = item[primaryKeyField];
                row[title.slice(title.indexOf(" ") + 1)] = item[fieldName];
            }

            if ((isIndexTable || isEmailTable) && item.hasOwnProperty('user_uid')) {
                row.user_uid = item.user_uid;
            }
            return row;
        });

        const worksheet = XLSX.utils.json_to_sheet(formattedData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, title.replace("Daftar ", ""));

        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const blob = new Blob([excelBuffer], { type: "application/octet-stream" });

        saveAs(blob, `${title.replace("Daftar ", "Data_")}.xlsx`);
    };

    const handleMenuClick = (event, row) => {
        setMenuAnchor(event.currentTarget);
        setSelectedRow(row);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
        setSelectedRow(null);
    };

    const handleEdit = async (row) => {
        handleMenuClose();
        setIsEditing(true);
        setCurrentId(row.uid || row[primaryKeyField]);
        setInitialUserGender(row.gender);
        setInitialUserDepartment(row.department);
        setInitialUserJabatan(row.jabatan); // Set initial Jabatan
        setInitialUserStatusPekerjaan(row.status_pekerjaan); // Set initial Status Pekerjaan
        setInitialUserStatusTenagaKerja(row.status_tenaga_kerja);
        setInitialUserRoles([...(row.roles || [])]); // Set initial roles, ensure it's an array
        setInitialUserFlowOrderDefault(row.id_flow_order_default || ''); // Set initial flow order default

        if (isUserTable) {
            setIsDetailView(true);
            setIsEditableDetail(false);
            setFieldValue(row.nama_user);
            setUserGender(row.gender);
            setUserJabatan(row.jabatan);
            setUserDepartment(row.department);
            setUserStatusPekerjaan(row.status_pekerjaan);
            setUserStatusTenagaKerja(row.status_tenaga_kerja);
            setUserRoles([...(row.roles || [])]); // Set current roles, ensure it's an array
            setSelectedRolesInDialog([...(row.roles || [])]); // Initialize dialog selection with current roles, ensure it's an array
            setUserEmail(row.email);
            setUserAvatar(row.avatar);
            setUserFlowOrderDefault(row.id_flow_order_default || ''); // Set current flow order default

            // Fetch nomor_index
            const indexRef = query(
                ref(database, "db_config/db_wfms/tb_index"),
                orderByChild("user_uid"),
                equalTo(row.uid)
            );
            const snapshot = await onValuePromise(indexRef);
            const indexData = snapshot.val();
            if (indexData) {
                const firstIndex = Object.values(indexData)[0];
                setUserIndex(firstIndex.nomor_index || '');
            } else {
                setUserIndex('N/A');
            }

        } else {
            setIsDetailView(false);
            setFieldValue(row[fieldName]);
        }
        setOpenDialog(true);
    };

    const handleDelete = (row) => {
        handleMenuClose();
        setSelectedRow(row);
        setOpenDeleteDialog(true);
    };

    const confirmDelete = async () => {
        if (!selectedRow) return;

        try {
            const idToDelete = isUserTable ? selectedRow.uid : selectedRow[primaryKeyField];
            const itemRef = ref(database, `${dbPath}/${idToDelete}`);
            await remove(itemRef);
            setSnackbar({
                open: true,
                message: `${title.slice(title.indexOf(" ") + 1)} berhasil dihapus.`,
                severity: "success",
            });
            setOpenDeleteDialog(false);
            setSelectedRow(null);
        } catch (error) {
            console.error("Gagal hapus:", error);
            setSnackbar({
                open: true,
                message: `Gagal menghapus data ${title.toLowerCase()}.`,
                severity: "error",
            });
        }
    };

    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false);
        setSelectedRow(null);
    };

    const handleToggleEditMode = (event) => {
        const willEditModeBeOn = event.target.checked;
        
        if (!willEditModeBeOn && isEditableDetail) {
            // Jika edit mode akan dimatikan tanpa update, kembalikan ke nilai awal
            setUserGender(initialUserGender);
            setUserDepartment(initialUserDepartment);
            setUserJabatan(initialUserJabatan); // Revert Jabatan
            setUserStatusPekerjaan(initialUserStatusPekerjaan); // Revert Status Pekerjaan
            setUserStatusTenagaKerja(initialUserStatusTenagaKerja);
            setUserRoles(initialUserRoles); // Revert roles
            setUserFlowOrderDefault(initialUserFlowOrderDefault); // Revert flow order default
        }
        
        setIsEditableDetail(willEditModeBeOn);
    };

    // --- Role Dialog Handlers ---
    const handleOpenRoleDialog = () => {
        setSelectedRolesInDialog([...userRoles]); // Initialize with current user roles
        setOpenRoleDialog(true);
    };

    const handleCloseRoleDialog = () => {
        setOpenRoleDialog(false);
        // Do not clear selectedRolesInDialog here, it will be reset on next open
    };

    const handleRoleCheckboxChange = (roleId) => {
        setSelectedRolesInDialog((prevSelected) => {
            if (prevSelected.includes(roleId)) {
                return prevSelected.filter((id) => id !== roleId);
            } else {
                return [...prevSelected, roleId];
            }
        });
    };

    const handleSaveRoles = () => {
        setUserRoles(selectedRolesInDialog);
        setOpenRoleDialog(false);
    };

    const handleDeleteRoleChipClick = (roleIdToDelete) => {
        const roleName = roleList.find(role => role.id === roleIdToDelete)?.name || roleIdToDelete;
        setRoleToDeleteId(roleIdToDelete);
        setRoleToDeleteName(roleName);
        setOpenRoleDeleteConfirmDialog(true);
    };

    const confirmDeleteRole = () => {
        setUserRoles((prevRoles) => prevRoles.filter((id) => id !== roleToDeleteId));
        setOpenRoleDeleteConfirmDialog(false);
        setRoleToDeleteId(null);
        setRoleToDeleteName('');
        setSnackbar({
            open: true,
            message: `Role "${roleToDeleteName}" berhasil dihapus.`,
            severity: "success",
        });
    };

    const handleCloseRoleDeleteConfirmDialog = () => {
        setOpenRoleDeleteConfirmDialog(false);
        setRoleToDeleteId(null);
        setRoleToDeleteName('');
    };
    // --- End Role Dialog Handlers ---


    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'inherit' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="h6">{title}</Typography>
                <Stack direction="row" spacing={1}>
                    {/* Tombol Add/Tambah: Sembunyikan untuk user jika penambahan hanya via autentikasi */}
                    {!isUserTable && (
                        <IconButton color="primary" size="small" onClick={handleOpenDialog}>
                            <AddIcon />
                        </IconButton>
                    )}
                    <IconButton color="primary" size="small" onClick={handleImportButtonClick}>
                        <FileUploadOutlinedIcon />
                    </IconButton>
                    <input
                        type="file"
                        accept=".xlsx, .xls"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        onChange={handleImportExcel}
                    />
                    <IconButton color="primary" size="small" onClick={exportToExcel}>
                        <FileDownloadOutlinedIcon />
                    </IconButton>
                </Stack>
            </Stack>

            <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell>Aksi</TableCell>
                            {isUserTable ? (
                                <>
                                    {/* Header untuk tabel user */}
                                    <TableCell>Nama</TableCell>
                                    <TableCell>Gender</TableCell>
                                    <TableCell>Departemen</TableCell>
                                    <TableCell>Jabatan</TableCell>
                                    <TableCell>Status Pekerjaan</TableCell>
                                    {/* <TableCell>Status Tenaga Kerja</TableCell> */} {/* Hidden as per request */}
                                    {/* <TableCell>Role</TableCell> */} {/* Hidden as per request */}
                                </>
                            ) : (
                                <>
                                    {/* Header untuk tabel lainnya */}
                                    <TableCell>Id</TableCell>
                                    <TableCell>{title.slice(title.indexOf(" ") + 1)}</TableCell>
                                    {isIndexTable && <TableCell>User UID</TableCell>}
                                    {isEmailTable && <TableCell>User UID</TableCell>}
                                </>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {dataList.length > 0 ? (
                            dataList
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((row) => (
                                    <TableRow key={row.uid || row[primaryKeyField]}>
                                        <TableCell>
                                            <IconButton size="small" onClick={(event) => handleMenuClick(event, row)}>
                                                {isUserTable ? <MoreHorizOutlinedIcon /> : <MoreVertIcon />}
                                            </IconButton>
                                        </TableCell>
                                        {isUserTable ? (
                                            <>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        {row.avatar ? (
                                                            <Avatar
                                                                src={row.avatar}
                                                                sx={{ width: 40, height: 40, mr: 2 }}
                                                            />
                                                        ) : (
                                                            <Avatar
                                                                sx={{
                                                                    width: 40,
                                                                    height: 40,
                                                                    mr: 2,
                                                                    bgcolor: 'primary.main',
                                                                    color: 'white'
                                                                }}
                                                            >
                                                                {row.nama_user ? row.nama_user.charAt(0).toUpperCase() : ''}
                                                            </Avatar>
                                                        )}
                                                        <Box>
                                                            <Typography variant="body1" fontWeight="medium">
                                                                {row.nama_user}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {row.email}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>{row.gender || 'N/A'}</TableCell>
                                                <TableCell>
                                                    {departments.find(dep => dep.id === row.department)?.name || 'N/A'}
                                                </TableCell>
                                                <TableCell>
                                                    {jabatanList.find(jab => jab.id === row.jabatan)?.name || 'N/A'}
                                                </TableCell>
                                                <TableCell>
                                                    {/* Display Status Pekerjaan as Chip */}
                                                    {row.status_pekerjaan ? (
                                                        <Chip
                                                            label={statusPekerjaanList.find(status => status.id === row.status_pekerjaan)?.name || row.status_pekerjaan}
                                                            color={statusPekerjaanList.find(status => status.id === row.status_pekerjaan)?.name?.toLowerCase() === 'on' ? 'success' : 'error'}
                                                            size="small"
                                                        />
                                                    ) : 'N/A'}
                                                </TableCell>
                                                {/* <TableCell>
                                                    {statusTenagaKerjaList.find(status => status.id === row.status_tenaga_kerja)?.name || 'N/A'}
                                                </TableCell> */} {/* Hidden as per request */}
                                                {/* <TableCell>
                                                    {(row.roles || []).length > 0 ? (
                                                        (row.roles || []).map(roleId => roleList.find(role => role.id === roleId)?.name || roleId).join(', ')
                                                    ) : 'N/A'}
                                                </TableCell> */} {/* Hidden as per request */}
                                            </>
                                        ) : (
                                            <>
                                                <TableCell>{row[primaryKeyField]}</TableCell>
                                                <TableCell>{row[fieldName]}</TableCell>
                                                {isIndexTable && <TableCell>{row.user_uid || 'Kosong'}</TableCell>}
                                                {isEmailTable && <TableCell>{row.user_uid || 'Kosong'}</TableCell>}
                                            </>
                                        )}
                                    </TableRow>
                                ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={isUserTable ? 6 : (isIndexTable || isEmailTable ? 4 : 3)} align="center"> {/* Adjusted colspan */}
                                    Tidak ada data {title.slice(title.indexOf(" ") + 1).toLowerCase()}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
            >
            {isUserTable ? (
                <MenuItem onClick={() => handleEdit(selectedRow)}>
                    <VisibilityOutlinedIcon fontSize="small" sx={{ mr: 2 }} />
                    Detail
                </MenuItem>
                ) : (
                <MenuItem onClick={() => handleEdit(selectedRow)}>
                    <EditIcon fontSize="small" sx={{ mr: 2 }} />
                    Edit
                </MenuItem>
                )}
                <MenuItem onClick={() => handleDelete(selectedRow)}>
                    <DeleteIcon fontSize="small" sx={{ mr: 2 }} />
                    Hapus
                </MenuItem>
            </Menu>

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: 2,
                }}
            >
                <Typography variant="body2">
                    {`${page * rowsPerPage + 1}-${Math.min(
                    (page + 1) * rowsPerPage,
                    dataList.length
                    )} of ${dataList.length}`}
                </Typography>
                <TablePagination
                    component="div"
                    count={dataList.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 20]}
                    labelRowsPerPage=""
                    labelDisplayedRows={() => ""}
                    sx={{
                        ".MuiTablePagination-toolbar": {
                            padding: 0,
                            minHeight: "auto",
                        },
                        ".MuiTablePagination-actions": {
                            display: "block",
                        },
                        ".MuiTablePagination-selectLabel": {
                            display: "none",
                        },
                        ".MuiTablePagination-displayedRows": {
                            display: "none",
                        },
                        ".MuiTablePagination-select": {
                            marginLeft: "8px",
                            marginRight: "8px",
                        },
                    }}
                />
            </Box>


            {/* Dialog Tambah/Edit/Detail */}
            <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
                    {isDetailView ? 'Detail User' : (isEditing ? `Edit ${title.slice(title.indexOf(" ") + 1)}` : `Tambah ${title.slice(title.indexOf(" ") + 1)}`)}
                    <Stack direction="row" alignItems="center" spacing={1}>
                        {isDetailView && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isEditableDetail}
                                        onChange={handleToggleEditMode}
                                        name="editToggle"
                                        color="primary"
                                    />
                                }
                                label={isEditableDetail ? "Edit On" : "Edit Off"}
                            />
                        )}
                        <IconButton aria-label="close" onClick={handleCloseDialog}>
                            <CloseIcon />
                        </IconButton>
                    </Stack>
                </DialogTitle>
                <DialogContent dividers>
                    {isDetailView ? (
                        <Box sx={{ textAlign: 'center', py: 0 }}>
                            {userAvatar ? (
                                <Avatar src={userAvatar} sx={{ width: 80, height: 80, mx: 'auto', mb: 1 }} />
                            ) : (
                                <Avatar sx={{ width: 80, height: 80, mx: 'auto', mb: 1, bgcolor: 'primary.main', color: 'white' }}>
                                    {fieldValue ? fieldValue.charAt(0).toUpperCase() : ''}
                                </Avatar>
                            )}
                            <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
                                {fieldValue}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {userIndex} | {userEmail}
                            </Typography>

                            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                <TextField
                                    margin="dense"
                                    label="Nama"
                                    type="text"
                                    sx={{ width: '50%' }}
                                    value={fieldValue}
                                    onChange={(e) => setFieldValue(e.target.value)}
                                    InputProps={{
                                        readOnly: !isEditableDetail,
                                    }}
                                />
                                {isEditableDetail ? (
                                    <FormControl sx={{ width: '50%', mt: 1 }} margin="dense">
                                        <InputLabel>Gender</InputLabel>
                                        <Select
                                            value={userGender}
                                            onChange={(e) => setUserGender(e.target.value)}
                                            label="Gender"
                                        >
                                            <MenuItem value="Male">Male</MenuItem>
                                            <MenuItem value="Female">Female</MenuItem>
                                        </Select>
                                    </FormControl>
                                ) : (
                                    <TextField
                                        margin="dense"
                                        label="Gender"
                                        type="text"
                                        sx={{ width: '50%' }}
                                        value={userGender}
                                        InputProps={{
                                            readOnly: true,
                                        }}
                                    />
                                )}
                            </Box>
                            
                            {/* Departemen and Jabatan fields */}
                            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                {isEditableDetail ? (
                                    <FormControl sx={{ width: '50%', mt: 1 }} margin="dense">
                                        <InputLabel>Departemen</InputLabel>
                                        <Select
                                            value={userDepartment}
                                            onChange={(e) => setUserDepartment(e.target.value)}
                                            label="Departemen"
                                        >
                                            {departments.map((dep) => (
                                                <MenuItem key={dep.id} value={dep.id}>{dep.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                ) : (
                                    <TextField
                                        margin="dense"
                                        label="Departemen"
                                        type="text"
                                        sx={{ width: '50%' }}
                                        value={departments.find(dep => dep.id === userDepartment)?.name || 'N/A'}
                                        InputProps={{
                                            readOnly: true,
                                        }}
                                    />
                                )}
                                {isEditableDetail ? (
                                    <FormControl sx={{ width: '50%', mt: 1 }} margin="dense">
                                        <InputLabel>Jabatan</InputLabel>
                                        <Select
                                            value={userJabatan}
                                            onChange={(e) => setUserJabatan(e.target.value)}
                                            label="Jabatan"
                                        >
                                            {jabatanList.map((jab) => (
                                                <MenuItem key={jab.id} value={jab.id}>{jab.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                ) : (
                                    <TextField
                                        margin="dense"
                                        label="Jabatan"
                                        type="text"
                                        sx={{ width: '50%' }}
                                        value={jabatanList.find(jab => jab.id === userJabatan)?.name || 'N/A'}
                                        InputProps={{
                                            readOnly: true,
                                        }}
                                    />
                                )}
                            </Box>

                            {/* Status Tenaga Kerja and Status Pekerjaan fields */}
                            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                {isEditableDetail ? (
                                    <FormControl sx={{ width: '50%', mt: 1 }} margin="dense">
                                        <InputLabel>Status Tenaga Kerja</InputLabel>
                                        <Select
                                            value={userStatusTenagaKerja}
                                            onChange={(e) => setUserStatusTenagaKerja(e.target.value)}
                                            label="Status Tenaga Kerja"
                                        >
                                            {statusTenagaKerjaList.map((status) => (
                                                <MenuItem key={status.id} value={status.id}>{status.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                ) : (
                                    <TextField
                                        margin="dense"
                                        label="Status Tenaga Kerja"
                                        type="text"
                                        sx={{ width: '50%' }}
                                        value={statusTenagaKerjaList.find(status => status.id === userStatusTenagaKerja)?.name || 'N/A'}
                                        InputProps={{
                                            readOnly: true,
                                        }}
                                    />
                                )}
                                {isEditableDetail ? (
                                    <FormControl sx={{ width: '50%', mt: 1 }} margin="dense">
                                        <InputLabel>Status Pekerjaan</InputLabel>
                                        <Select
                                            value={userStatusPekerjaan}
                                            onChange={(e) => setUserStatusPekerjaan(e.target.value)}
                                            label="Status Pekerjaan"
                                        >
                                            {statusPekerjaanList.map((status) => (
                                                <MenuItem key={status.id} value={status.id}>{status.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                ) : (
                                    <TextField
                                        margin="dense"
                                        label="Status Pekerjaan"
                                        type="text"
                                        sx={{ width: '50%' }}
                                        value={statusPekerjaanList.find(status => status.id === userStatusPekerjaan)?.name || 'N/A'}
                                        InputProps={{
                                            readOnly: true,
                                        }}
                                    />
                                )}
                            </Box>

                            {/* New: Default Flow Order field */}
                            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                {isEditableDetail ? (
                                    <FormControl fullWidth margin="dense">
                                        <InputLabel>Alur Persetujuan Default</InputLabel>
                                        <Select
                                            value={userFlowOrderDefault}
                                            onChange={(e) => setUserFlowOrderDefault(e.target.value)}
                                            label="Alur Persetujuan Default"
                                        >
                                            <MenuItem value="">
                                                <em>None</em>
                                            </MenuItem>
                                            {flowConfigurations.map((flow) => (
                                                <MenuItem key={flow.id} value={flow.id}>
                                                    {flow.flow_name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                ) : (
                                    <TextField
                                        margin="dense"
                                        label="Alur Persetujuan Default"
                                        type="text"
                                        fullWidth
                                        value={flowConfigurations.find(flow => flow.id === userFlowOrderDefault)?.flow_name || 'N/A'}
                                        InputProps={{
                                            readOnly: true,
                                        }}
                                    />
                                )}
                            </Box>

                            {/* New: Role field and Add Role button */}
                            <Box sx={{ mb: 0 }}>
                                <Typography variant="subtitle1" sx={{ mb: 2, textAlign: 'left' }}>
                                    Role
                                </Typography>
                                <Box sx={{ mt: 1, mb: 1, display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-start' }}>
                                    {(userRoles || []).map(roleId => (
                                        <Chip
                                            key={roleId}
                                            label={roleList.find(role => role.id === roleId)?.name || roleId}
                                            onDelete={isEditableDetail ? () => handleDeleteRoleChipClick(roleId) : undefined} // Changed to open confirmation
                                            color="primary"
                                            variant="outlined"
                                        />
                                    ))}
                                </Box>
                                {isEditableDetail && (
                                    <Button
                                        variant="outlined"
                                        onClick={handleOpenRoleDialog}
                                        sx={{ mt: 1, float: 'left' }} // Position to the left
                                    >
                                        Tambah Role
                                    </Button>
                                )}
                                <Box sx={{ clear: 'both' }} /> {/* Clear float */}
                            </Box>
                        </Box>
                    ) : (
                        <TextField
                            autoFocus
                            margin="dense"
                            label={title.slice(title.indexOf(" ") + 1)}
                            type="text"
                            fullWidth
                            value={fieldValue}
                            onChange={(e) => setFieldValue(e.target.value)}
                        />
                    )}
                </DialogContent>
                <DialogActions sx={{ pt: "20px", pb: "20px", pl: "24px", pr: "24px" }}>
                    <Button onClick={handleCloseDialog}>Batal</Button>
                    {/* Show Save/Update button for non-user tables, or for user table when in editable detail mode */}
                    {(!isUserTable || (isUserTable && isEditableDetail)) && (
                        <Button onClick={handleSave} variant="contained">
                            {isEditing ? "Update" : "Simpan"}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Dialog Tambah Role */}
            <Dialog open={openRoleDialog} onClose={handleCloseRoleDialog} fullWidth maxWidth="xs">
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
                    Tambah Role
                    <IconButton aria-label="close" onClick={handleCloseRoleDialog}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {roleList.length > 0 ? (
                        <FormControl component="fieldset" variant="standard">
                            {roleList.map((role) => (
                                <FormControlLabel
                                    key={role.id}
                                    control={
                                        <Checkbox
                                            checked={selectedRolesInDialog.includes(role.id)}
                                            onChange={() => handleRoleCheckboxChange(role.id)}
                                            name={role.name}
                                        />
                                    }
                                    label={role.name}
                                />
                            ))}
                        </FormControl>
                    ) : (
                        <Typography>Tidak ada role tersedia.</Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ pt: "20px", pb: "20px", pl: "24px", pr: "24px" }}>
                    <Button onClick={handleCloseRoleDialog}>Batal</Button>
                    <Button onClick={handleSaveRoles} variant="contained">
                        Simpan
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog Konfirmasi Hapus Role Chip */}
            <Dialog open={openRoleDeleteConfirmDialog} onClose={handleCloseRoleDeleteConfirmDialog} maxWidth="xs" fullWidth>
                <DialogTitle>Konfirmasi Hapus Role</DialogTitle>
                <DialogContent>
                    <Typography>
                        Apakah Anda yakin ingin menghapus role "{roleToDeleteName}" dari user ini?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ pt: "0px", pb: "20px", pl: "24px", pr: "24px" }}>
                    <Button onClick={handleCloseRoleDeleteConfirmDialog}>Batal</Button>
                    <Button
                        onClick={confirmDeleteRole}
                        variant="contained"
                        color="error"
                    >
                        Hapus
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog Konfirmasi Hapus Data Tabel Utama */}
            <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog} maxWidth="xs" fullWidth>
                <DialogTitle>Konfirmasi Hapus</DialogTitle>
                <DialogContent>
                    <Typography>
                        Apakah Anda yakin ingin menghapus {isUserTable ? `user "${selectedRow?.nama_user}"` : `${title.slice(title.indexOf(" ") + 1).toLowerCase()} "${selectedRow?.[fieldName]}"`}?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ pt: "0px", pb: "20px", pl: "24px", pr: "24px" }}>
                    <Button onClick={handleCloseDeleteDialog}>Batal</Button>
                    <Button
                        onClick={confirmDelete}
                        variant="contained"
                        color="error"
                    >
                        Hapus
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default WorkforceManagementConfig;
